import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { closeDialog } from '@shared/store/dialog.duck';
import {
  selectFloorForReservation, SelectionState,
  selectSelectionOffice,
  setSelectionState
} from '@features/desks/store/deskReservation.duck';
import { MobileOnly } from "@shared/ui/atoms/DeviceSpecific";
import { GoBackButton } from "@shared/ui/atoms/buttons/GoBackButton";
import DialogHeader from "@shared/dialogs/DialogHeader";
import FloorLocationContentCard from "@shared/ui/molecules/FloorLocationContentCard";
import { Center, Row } from "@shared/ui/atoms/StructuralLayout";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { selectAllOfficeEntities } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { CurrentOfficeWhereaboutsSelection } from "./CurrentOfficeWhereaboutsSelection";
import { warningNotification } from '@shared/store/notification.duck';
import { WithOfficeChildren } from '@features/desks/hooks/WithOfficeChildren';
import { Moment } from "moment";
import { WithSeatsAvailableInParentOffice } from '@features/desks/hooks/WithSeatsAvailableInParentOffice';
import { CapacityResponse, OfficeWithCapacity } from '@features/desks/services/OfficeCapacityService';
import { LoadingSpinner } from "@shared/ui/atoms/LoadingSpinner";
import styled from "styled-components";
import { selectActiveDay } from '@features/whereabouts/store/editMovements.duck';
import { BodyLarge } from "@shared/ui/atoms/fonts/Body";
import { smallTablet } from "@shared/ui/atoms/MediaQueries";

interface Props {
  activeDate: Moment;
}

export function ChooseFloor(props: Props) {
  const dispatch = useDispatch();
  const close = () => dispatch(closeDialog());
  const offices = useSelector(selectAllOfficeEntities);
  const selectedOffice = useSelector(selectSelectionOffice);
  const selectedOfficeChildren = WithOfficeChildren(selectedOffice?.id);
  const date = useSelector(selectActiveDay);

  const {isLoading, capacities} = WithSeatsAvailableInParentOffice(props.activeDate, selectedOffice);

  const officesWithCapacities: OfficeWithCapacity[] = useMemo(() => {
    return selectedOfficeChildren.map((o: OfficeEntity) => {
      const capacity = capacities.find((c: CapacityResponse) => c.officeId === o.id);
      if (!capacity) return undefined;
      return {
        capacity: capacity,
        office: o,
      }
    }).filter(owc => !!owc) as OfficeWithCapacity[];
  }, [selectedOfficeChildren, capacities]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (selectedOfficeChildren.length === 0 && selectedOffice?.hasCarParkPlan) {
      dispatch(setSelectionState(SelectionState.Done))
    }
  }, [dispatch, isLoading, selectedOffice, selectedOfficeChildren])

  const onSelectFloor = (floor: OfficeEntity) => {
    if (floor.active) {
      dispatch(selectFloorForReservation(offices.find((o: OfficeEntity) => o.id === floor.id)));
    } else {
      dispatch(warningNotification('This floor is not active.'));
    }
  };

  // TODO Show selected floor loading spinner
  return (
    <>
      <MobileOnly>
        <GoBackButton onClick={close} text="back-to-whereabouts"/>
      </MobileOnly>
      <DialogHeader title="dialog-headers.choose-floor"/>
      <BodyLarge weight={600} style={{marginBottom: 16}}>{date?.format('dddd DD MMMM')}</BodyLarge>

      <CurrentOfficeWhereaboutsSelection/>

      {isLoading ? <>
        <Center>
          <LoadingSpinner hideBorder={true} />
        </Center>
      </> : <>
        {officesWithCapacities.length > 0 && <WrappedRow>
          {officesWithCapacities.map((floor: OfficeWithCapacity, key: number) => (
            <FloorLocationContentCard onClick={() => onSelectFloor(floor.office)}
                                      activeDate={props.activeDate}
                                      capacity={floor.capacity}
                                      key={key}
                                      floor={floor.office} />
          ))}
        </WrappedRow>}
      </>}
    </>
  )
}

const WrappedRow = styled(Row)`
  flex-wrap: wrap;
  margin-bottom: 24px;
  overflow-y: auto;
  .dialog__title {
    margin-bottom: 0;
  }
  @media (${smallTablet}) {
    max-height: 40vh;
  }
`
